import { unref, computed, type MaybeRefOrGetter } from "vue";
import { useHead, useRoute, useRuntimeConfig } from "#imports";
import { useI18n } from "vue-i18n";

type ImageFormat = {
  url: string;
};

type ImageData = {
  url: string;
  formats?: {
    [key: string]: ImageFormat;
  };
};

type SeoData = {
  title?: string;
  description?: string;
  keywords?: string;
  canonicalURL?: string;
  noIndex?: boolean;
  image?: ImageData;
  schema?: object; // JSON-LD schema.org objesi
};

export function useMetaTags(
  seoData: MaybeRefOrGetter<SeoData> = {},
  pageTitle?: MaybeRefOrGetter<string | undefined>,
  pageDescription?: MaybeRefOrGetter<string | undefined>,
) {
  const { t, locale } = useI18n();
  const route = useRoute();
  const runtimeConfig = useRuntimeConfig();
  const baseUrl = runtimeConfig.public.baseUrl;
  const siteUrl = runtimeConfig.public.siteUrl;

  useHead(() => {
    const resolveVar = (val: any) => typeof val === 'function' ? val() : unref(val);
    const s = resolveVar(seoData) as SeoData;
    const pt = resolveVar(pageTitle) as string | undefined;
    const pd = resolveVar(pageDescription) as string | undefined;

    const imageUrl = s.image?.formats?.large?.url ?? s.image?.url;
    const title = s.title || pt || t("Meta.Title");
    const description = s.description || pd || t("Meta.Description");
    const keywords = s.keywords || t("Meta.Keywords");
    const robotsContent =
      s.noIndex === true ? "noindex, nofollow" : "index, follow";

    const canonicalURL =
      s.canonicalURL ||
      siteUrl + (route.path === "/" ? "" : route.path);

    // Fallback OG image must point to a file that actually exists in /public
    // (there is no /logo.png — that URL 404s). pwa-512x512.png is the brand icon.
    const imageURL = imageUrl ? baseUrl + imageUrl : siteUrl + "/pwa-512x512.png";

    // og:locale must be a language_TERRITORY code; list alternates so social
    // crawlers know the page has other-language versions.
    const OG_LOCALE: Record<string, string> = { tr: "tr_TR", en: "en_US", ar: "ar_SA" };
    const currentOg = OG_LOCALE[locale.value] || "tr_TR";
    const altOgLocales = Object.entries(OG_LOCALE)
      .filter(([code]) => code !== locale.value)
      .map(([, og]) => ({ property: "og:locale:alternate", content: og }));

    return {
      title,
      htmlAttrs: {
        lang: locale.value,
      },
      link: canonicalURL ? [{ rel: "canonical", href: canonicalURL }] : [],
      meta: [
        { name: "description", content: description },
        { name: "keywords", content: keywords },
        { name: "robots", content: robotsContent },
        { name: "googlebot", content: robotsContent },
        { property: "og:title", content: title },
        { property: "og:description", content: description },
        { property: "og:type", content: "website" },
        { property: "og:url", content: canonicalURL },
        { property: "og:locale", content: currentOg },
        ...altOgLocales,
        { property: "og:site_name", content: "Esfolyo" },
        { property: "og:image", content: imageURL || "" },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:title", content: title },
        { name: "twitter:description", content: description },
        { name: "twitter:image", content: imageURL || "" },
        { name: "ai-crawl", content: "yes" },
      ],
    };
  });
}
